
import { Component, Prop, Vue, Watch } from "vue-property-decorator"
import { namespace, Getter, State } from "vuex-class"
import { CaseColumn, caseColumns } from "@/types/caseColumns"
import { Clause, getDefaultSearchClauseParameters, SearchClauseParameters } from "@/types/view"

import vSelect from "vue-select"
import ParameterValue from "../Controls/ParameterValue.vue"
import { DataType, DynamicParameter, Operator, operatorLabels, operatorsByFieldType } from "@/types/insights"
import CalculationDataType, { Field } from "@/types/field"
import { CalculationType, FieldInstance } from "@/types"
import Guid from "@/scripts/guid"
import { dataTypeParameters } from "@/types/dataType"
import api from "@/services/api"
import { UserColumn } from "@/types/userColumns"
import { OperatorLabel } from "@/types/insights/operators"
import { CurrentUser } from "@/types/user"
import { UserRole } from "@/types/caseUser"

@Component({
	components: {
		"v-select": vSelect,
		"parameter-value": ParameterValue,
		"filters": () => import("./InsightFilters.vue")
	}
})
export default class InsightFilters extends Vue {
	@Prop()
	value!: Clause[]

	@Prop()
	fields!: CaseColumn[]

	@Prop()
	recordName!: string

	@Prop({ default: true })
	allowOr!: boolean

	@Prop({ default: () => [] })
	dynamicParameters!: DynamicParameter[] | null

	@Prop({ required: true })
	userFields!: UserColumn[]

	@State me!: CurrentUser;

	filterColumns: any[] = [
		{
			key: "valid",
			label: ""
		},
		{
			key: "field",
			label: "Field"
		},
		{
			key: "operator",
			label: "Operator"
		},
		{
			key: "value",
			label: "Value"
		},
		{
			key: "buttons",
			label: ""
		}
	]
	clauses: Clause[] = []
	private Operator = Operator

	get filtersValid () : boolean {
		return this.clauses.every(clause => this.isFilterValid(clause))
	}

	get parameterTypes () {
		return dataTypeParameters.toDictionary("dataType")
	}

	@Watch("value")
	onValueChanged () {
		this.clauses = this.value
	}

	@Watch("filtersValid")
	onFiltersValueChanged (nowValid: boolean) {
		this.$emit("validChanged", this.filtersValid)
	}

	async mounted () {
		this.clauses = this.value

		let fieldDataSet = false
		let userFieldDataSet = false

		this.clauses.forEach(clause => {
			clause.key = Guid.newGuid()
			if (!clause.fieldData) {
				Vue.set(clause, "fieldData", this.fields.find(f => f.key === clause.field))
				fieldDataSet = true
			}

			if (!clause.userFieldData) {
				Vue.set(clause, "userFieldData", this.userFieldsWithSubroles(clause).find(f => f.key === clause.userField))
				Vue.set(clause, "userField", clause.userField)

				userFieldDataSet = true
			}
		})

		if (fieldDataSet || userFieldDataSet) {
			this.$emit("input", this.clauses)
		}

		this.$emit("validChanged", this.filtersValid)
	}

	clausesChanged () {
		this.clauses.forEach(clause => {
			if (clause.dynamicParameter) {
				if (!this.dynamicParametersForClause(clause).find(p => p.key === clause.dynamicParameter)) {
					clause.dynamicParameter = null
				}
			}
		})

		this.$emit("input", this.clauses)
	}

	getValidOperators (clause: Clause): OperatorLabel[] {
		if (clause.userFieldData) {
			return this.getValidOperatorsForField(clause.userFieldData)
		}

		return this.getValidOperatorsForField(clause.fieldData)
	}

	getValidOperatorsForField (field: CaseColumn): OperatorLabel[] {
		const dataType = field.dataType === "calculated" && field.fieldExtensions ? CalculationDataType[field.fieldExtensions.calculationType as CalculationType] : field.dataType

		var operators = field && field.operators ? field.operators : operatorsByFieldType[dataType]
		if (!operators) {
			return []
		}
		return operatorLabels.filter((o: any) => operators.indexOf(o.operator) > -1)
	}

	resetUserField (clause: Clause) {
		Vue.set(clause, "userFieldData", null)
		Vue.set(clause, "userField", null)

		clause.userFieldDataType = undefined
	}

	fieldChanged (clause: Clause) {
		this.resetUserField(clause)

		if (!clause.fieldData || this.getValidOperatorsForField(clause.fieldData)
			.find(o => o.operator === clause.operator)) {
			clause.operator = null
		}

		clause.value = null
		clause.field = clause.fieldData ? clause.fieldData.key : null
		clause.fieldDataType = clause && clause.fieldData ? clause.fieldData.fieldDataType : 0

		clause.dynamicField = clause && clause.fieldData && clause.fieldData.dynamicField !== undefined ? clause.fieldData.dynamicField : false
		clause.dynamicParameter = null

		this.clausesChanged()
	}

	userFieldChanged (clause: Clause) {
		Vue.set(clause, "operator", null)
		clause.value = null
		Vue.set(clause, "userField", clause.userFieldData ? clause.userFieldData.key : null)
		clause.userFieldDataType = clause && clause.userFieldData ? clause.userFieldData.fieldDataType : 0
		clause.dynamicField = clause && clause.userFieldData ? !!clause.userFieldData.dynamicField : false
		clause.dynamicParameter = null

		this.clausesChanged()
	}

	dynamicValueChanged (clause: any, newValue: boolean) {
		Vue.set(clause, "dynamicValue", newValue === null ? false : newValue)
		Vue.set(clause, "value", null)

		this.clausesChanged()
	}

	searchClauseParametersChanged (clause: Clause, searchClauseParameters: SearchClauseParameters) {
		Vue.set(clause, "searchClauseParameters", searchClauseParameters)

		this.clausesChanged()
	}

	addFilter () {
		this.clauses.push({
			key: Guid.newGuid(),
			dynamicField: false,
			dynamicValue: false,
			fieldDataType: 0,
			fieldData: null,
			label: "",
			value: null,
			searchClauseParameters: getDefaultSearchClauseParameters(),
			dynamicParameter: null
		})
	}

	addOrFilter () {
		this.clauses.push({
			key: Guid.newGuid(),
			dynamicField: false,
			dynamicValue: false,
			fieldDataType: 0,
			label: "",
			value: null,
			orClause: true,
			subClauses: [],
			isValid: true,
			searchClauseParameters: getDefaultSearchClauseParameters()
		})
	}

	removeFilter (filter: Clause) {
		this.clauses.splice(this.clauses.indexOf(filter), 1)
		this.clausesChanged()
	}

	isFilterValid (filter: Clause): boolean {
		if (filter.orClause) {
			return !!filter.isValid && !!filter.subClauses && (filter.subClauses.length > 0)
		}

		return this.caseFieldFilterValid(filter) || this.userFieldFilterValid(filter)
	}

	private caseFieldFilterValid (filter: Clause) : boolean {
		return !!filter.fieldData &&
			filter.operator !== undefined &&
			filter.operator !== null &&
			(filter.operator === Operator.IsSet || filter.operator === Operator.NotSet || (!!filter.dynamicParameter || (filter.value !== undefined && filter.value !== null)))
	}

	private userFieldFilterValid (filter: Clause) : boolean {
		return filter &&
			!!filter.userFieldData &&
			filter.operator !== undefined &&
			filter.operator !== null &&
			(filter.operator === Operator.IsSet || filter.operator === Operator.NotSet || (!!filter.dynamicParameter || (filter.value !== undefined && filter.value !== null)))
	}

	private userFieldsWithSubroles (clause: Clause): CaseColumn[] {
		return [
			...this.userFields,
			...(clause.fieldData && clause.fieldData.fieldExtensions && clause.fieldData.fieldExtensions.subroles
				? clause.fieldData.fieldExtensions.subroles.map((subRole: UserRole) => {
					return {
						label: "Subrole: " + subRole.title,
						key: "subrole." + subRole.title,
						dataType: "subrole",
						fieldDataType: DataType.subrole
					}
				})
				: []
			)
		]
	}

	setValid (filter: any, valid: boolean) {
		Vue.set(filter, "isValid", valid)
		this.clausesChanged()
	}

	dynamicParametersForClause (clause: Clause): DynamicParameter[] {
		if (!this.dynamicParameters) return []

		if (clause.operator === null ||
			clause.operator === undefined ||
			clause.operator === Operator.IsSet ||
			clause.operator === Operator.NotSet) {
			return []
		}

		return this.dynamicParameters!.filter(dynamicParameter =>
			this.dynamicParameterValidForClause(clause, dynamicParameter)
		)
	}

	private dynamicParameterValidForClause (clause: Clause, dynamicParameter: DynamicParameter) : boolean {
		let fieldData = clause.fieldData
		let fieldDataType = clause.fieldDataType
		if (this.isUserRoleWithUserFieldSet(clause)) {
			fieldData = clause.userFieldData
			fieldDataType = clause.userFieldDataType!
		}

		if (dynamicParameter.dataType === DataType.array && !!dynamicParameter.dataSourceId) {
			return fieldData.dataSourceId === dynamicParameter.dataSourceId
		} else if (fieldDataType === DataType.dateTime) {
			if (clause.operator === Operator.DateRange) {
				return dynamicParameter.dataType === DataType.dateRange
			} else {
				return dynamicParameter.dataType === DataType.dateTime
			}
		} else if (this.parameterTypes[dynamicParameter.dataType]) {
			return this.parameterTypes[dynamicParameter.dataType].fieldDataTypes.includes(this.getDynamicParameterClauseDataType(clause, fieldDataType))
		} else {
			return false
		}
	}

	private getDynamicParameterClauseDataType (clause: Clause, fieldDataType: DataType) : DataType {
		if (fieldDataType !== DataType.userRole) {
			return fieldDataType
		}

		if (clause.operator === Operator.Equals ||
			clause.operator === Operator.GreaterThan ||
			clause.operator === Operator.LessThan) {
			return DataType.number
		} else {
			if (clause.operator === Operator.InTeam) {
				return DataType.team
			}
		}
		return fieldDataType
	}

	private isUserRoleField (clause: Clause) : boolean {
		return clause.fieldDataType === DataType.userRole ||
			(!clause.dynamicField && clause.fieldData && (clause.fieldData as CaseColumn).key === "assigneeId") || // Assignee
			(!clause.dynamicField && clause.fieldData && (clause.fieldData as CaseColumn).key === "users") // Collaborators
	}

	private isUserRoleWithUserFieldSet (clause: Clause) : boolean {
		return this.isUserRoleField(clause) && !!clause.userFieldData
	}

	private getColumnForParameterValue (clause: Clause): CaseColumn {
		if (clause.userField != null) {
			return clause.userFieldData
		}
		return clause.fieldData
	}

	/** Hides clasues that are adminOnly if the user isn't admin */
	private getNonAdminClauses (clause: Clause) {
		if (!this.me.isAdmin) {
			var caseCol = caseColumns.find(col => col.key === clause.field)
			if (caseCol !== undefined && caseCol.adminOnly) {
				return false	// Clause is admin only and user isn't admin.
			} else return true	// Clause isn't admin only.
		} else return true		// User is admin.
	}
}

